import { createSelector } from "reselect"
import { get, chain, isEmpty, map, identity, isObject, pick, omit } from "lodash"
import { getRawTopic } from "modules/research-topic-root/selectors"
import { getTickerAndMicFromContributor, formatLabel, getTickerMicAndId, getAssetClassFromType } from "helpers/securities"
import { squashUnmodeledBaskets } from "helpers/swaps"

const getRawInstrumentPortfolioPositions = (_, props) => get(props, "instrumentPortfolioPositions")
const getPropsSmartTradePositionSet = (_, props) => get(props, "smartTradePositionSet")
const getInstrumentDescriptors = (_, props) => get(props, "instrumentDescriptors")
const getSelectedSegment = (_, props) => get(props, "segment")
export const getSelectedExperiment = (_, props) => get(props, "experiment")

export const getSmartTradePositionSet = createSelector(
  [getPropsSmartTradePositionSet, getSelectedExperiment, getRawTopic, getSelectedSegment],
  (propsSmartTradePositionSet, selectedExperiment, topic, segment) =>
    selectedExperiment
      ? ({ type: "RESEARCH_TOPIC", id: topic.id, experimentId: selectedExperiment.id, segment })
      : squashUnmodeledBaskets(propsSmartTradePositionSet),
)

export const getInstrumentCompositionPositions = createSelector(
  [getRawInstrumentPortfolioPositions, getInstrumentDescriptors],
  (instrumentPortfolioPositions, securityInstrumentDescriptors) => {
    if (isEmpty(instrumentPortfolioPositions)) {
      // Comparison functions down the line expects to have an object of the following format:
      // { id: { ticker, mic, ... }, assetClass: 'equity' }
      return [{ id: omit(securityInstrumentDescriptors, ["assetClass"]), assetClass: get(securityInstrumentDescriptors, "assetClass") }]
    }

    // Because we use getTickerAndMicFromContributor() inside getRawSmartTradePositions(selector) to get position descriptors
    // And the function adds fake mics and tickers, we need to apply the same logic to the instrumentPortfolioPositions
    return chain(instrumentPortfolioPositions)
      .pick(["equities", "swaps", "currencies", "fixedIncome", "indexes", "otherAssets", "commodities"])
      .flatMap((positions, type) => map(positions, position => {
        const assetClass = getAssetClassFromType(type)
        let id = position.id
        if (assetClass === "fixedIncome") {
          id = position.id.isin
        } else if (assetClass === "index") {
          id = position.id.indexId
        }
        return {
          id: assetClass === "equity" ? id : getTickerMicAndId(assetClass, id),
          assetClass,
        }
      }))
      .filter(identity)
      .value()
  },
)

// For both getIdTickerAndMicFromItem and formatItemLabel, it is possible to receive the id in two formats
// 1. { id: 'TICKER.MIC', ticker: 'TICKER', mic: 'MIC' } for positions coming from the Simulation.Composition node
// 2. 'TICKER.MIC' for positions coming from the Portfolio.Dates (non equities)
export function getIdTickerAndMicFromItem(item) {
  if (isObject(item.id)) {
    return pick(item.id, ["id", "ticker", "mic"])
  }

  const { ticker, mic } = getTickerAndMicFromContributor({ id: item.id, assetClass: item.assetClass })
  return { id: item.id, ticker, mic }
}

export function formatItemLabel(item) {
  const { ticker } = getIdTickerAndMicFromItem(item)
  return formatLabel({ ticker, country: item.country })
}
